import * as React from "react";
import { Link as ReactRouterLink, LinkProps as ReactRouterLinkProps } from "react-router-dom";
import { Rover, RoverProps } from "reakit/Rover";
import styled, { css } from "styled-components";
import { BaseLink, LinkProps } from "./BaseLink";
import { DropdownHandler, DesktopAlignmentTypes } from "./DropdownHandler";
import { Icon } from "./Icon";
import { ListItem } from "./ListItem";
import { useKeyboardNav } from "./hooks/useKeyboardNav";

import { mediaQueries, useGetBoundingClientRectRef } from "./style";
import { MediaQueryWrapper, style } from "./style";
import { focusOutline } from "./utils/focusOutlineStyle";
import { Tooltip, TooltipPlacement } from "./Tooltip";

type DropdownMenuItemProps = ActionItemProps & {
  title: string;
  action: (closeFn: () => void) => void;
};

type CustomMenuItemProps = ActionItemProps & {
  rover?: any;
  isManagingFocus?: boolean;
  setManagingFocus?: (value: React.SetStateAction<boolean>) => void;
  onClick?: () => void;
  hasBorderTop?: boolean;
};

export type DropdownProps = {
  isMenuOpen?: boolean;
  menuItems?: DropdownMenuItemProps[];
  mobileTitle?: string;
  closeOnSelect?: boolean;
  DropdownTriggerElement?: React.ComponentType<any>;
  desktopAlignment?: DesktopAlignmentTypes;
  "data-testid"?: string;
};

type ActionItemProps = {
  disabled?: boolean;
  danger?: boolean;
  hasTopBorder?: boolean;
  tooltip?: {
    content: string;
    placement?: TooltipPlacement;
  };
};

type LinkStyleProps = {
  $danger?: boolean;
  $hasTopBorder?: boolean;
};

type DropdownIconProps = {
  showHoverState: boolean;
};

export type DropdownCellProps = {
  isExpanded?: boolean;
  firstColumnOnlyBreakpoint?: string | MediaQueryWrapper;
  mobileFirstColumnOnly?: boolean;
};

const DROPDOWN_ICON_SIZE = "40px";
const DROPDOWN_ICON_HEIGHT_SMALL = "20px";
const DROPDOWN_MENU_MOBILE_MAX_BREAKPOINT = mediaQueries.smallMax;

const DropdownMenuItems = styled.ul`
  padding-left: 0;
  text-align: left;
  margin: 0;
  border: ${style.vars.borderWidth.thin} solid ${style.colors.shadesOfGrey["4"]};
  border-radius: ${style.vars.borderRadius.base};
  color: ${style.colors.grey.darkest};
  overflow: hidden;

  ${DROPDOWN_MENU_MOBILE_MAX_BREAKPOINT} {
    text-align: left;
  }
`;

const DropdownMenuItem = styled.li`
  list-style: none;
`;

const RoverItem: React.FC<RoverProps & ActionItemProps> = ({ children, ...rest }) => {
  return (
    <Rover as={RoverActionItem} {...rest}>
      {children}
    </Rover>
  );
};

const ActionItemText = styled.div`
  padding-left: ${style.vars.space["3"]};
  padding-right: ${style.vars.space["3"]};
`;

const RoverActionItem = styled.div<ActionItemProps>`
  padding: ${style.vars.space["2"]};
  cursor: pointer;

  &:hover {
    background: ${style.colors.grey.light};
    font-weight: ${style.vars.fontWeight.bold};
  }

  &:active {
    color: ${style.colors.blue.primary};
  }

  ${({ danger }) =>
    danger &&
    css`
      color: ${style.colors.red.dark};
      &:hover {
        color: ${style.colors.red.primary};
      }
    `};
`;

const GeneratedActionItem = styled(RoverItem)<ActionItemProps>`
  ${({ danger, hasTopBorder }) =>
    (danger || hasTopBorder) &&
    css`
      border-top: ${style.vars.borderWidth.thin} solid ${style.colors.shadesOfGrey["4"]};
    `};
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: default;
      pointer-events: none;
      &:hover {
        color: initial;
      }
    `};
  &:focus {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: -${style.vars.whitespace.xsmall};
  }
`;

const ListItemRoverItem = styled(RoverItem)<ActionItemProps>`
  padding: 0px;
  ${({ danger, hasTopBorder }) =>
    (danger || hasTopBorder) &&
    css`
      border-top: ${style.vars.borderWidth.thin} solid ${style.colors.shadesOfGrey["4"]};
    `};
  &:focus {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: -${style.vars.whitespace.xsmall};
  }
`;

export const TableDropdownCell = styled.div<DropdownCellProps>`
  padding: ${style.vars.space["1"]} ${style.vars.space["3"]};
  .is-medium & {
    padding: ${style.vars.space["2"]} ${style.vars.space["3"]};
  }
  .is-large & {
    padding: ${style.vars.space["3"]};
  }
  ${({ firstColumnOnlyBreakpoint, mobileFirstColumnOnly, isExpanded }) => css`
    ${firstColumnOnlyBreakpoint} {
      position: absolute;
      right: 0;
      ${!mobileFirstColumnOnly &&
      css`
        .is-medium &,
        .is-large & {
          top: 0;
        }
      `}
      ${isExpanded &&
      css`
        top: 0;
      `}
    }
  `};
`;

const DropdownWrapper = styled.div<DropdownProps>`
  position: relative;
`;

const alignmentTranslations = {
  center: "-50%",
  left: "0%",
  right: "-100%",
};

const DropdownMenuWrapper = styled.div<{
  desktopAlignment: keyof typeof alignmentTranslations;
  offsetY: number;
  maxHeight: number;
}>`
  width: max-content;
  max-width: 270px;
  min-width: 200px;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: ${({ maxHeight }) => `calc(100vh - ${maxHeight}px)`};
  transform: ${({ desktopAlignment, offsetY }) => {
    const x = alignmentTranslations[desktopAlignment] || "0%";
    const y = offsetY || 0;
    return `translate(${x}, -${y}px)`;
  }};
  box-shadow: 0px ${style.vars.whitespace.xsmall} ${style.vars.whitespace.xsmall} 0px rgba(3, 27, 78, 0.25),
    inset 0 0 0 ${style.vars.borderWidth.thin} ${style.colors.shadesOfGrey["4"]};
  background: ${style.colors.white};
  border-radius: ${style.vars.borderRadius.base};
  margin-top: ${style.vars.whitespace.xsmall};
  ${DROPDOWN_MENU_MOBILE_MAX_BREAKPOINT} {
    max-width: unset;
    min-width: unset;
    width: 100%;
    transform: none;
    ${RoverActionItem} {
      padding: 0.75rem ${style.vars.space["4"]};
    }
    ${ListItemRoverItem} {
      padding: 0px;
    }
  }
`;

const DropdownIcon = styled(Icon)`
  width: ${DROPDOWN_ICON_SIZE};
  height: ${DROPDOWN_ICON_SIZE};
  color: ${style.colors.grey.dark};
  ${mediaQueries.mediumMin} {
    .is-small & {
      height: ${DROPDOWN_ICON_HEIGHT_SMALL};
    }
  }
`;

const DropdownIconWrapper = styled.div<DropdownIconProps>`
  position: relative;
  cursor: pointer;
  border-radius: ${style.vars.borderRadius.base};
  /* Keep hover state when menu is open */
  ${({ showHoverState }) =>
    showHoverState &&
    css`
      background-color: ${style.colors.grey.lightest};
      pointer-events: none;
    `}
  &:focus {
    ${focusOutline()};
  }
  &:hover {
    background-color: ${style.colors.grey.lightest};
  }
`;

const recursiveMap = (children: React.ReactNode, props: any): any => {
  return React.Children.map(children, (child: React.ReactNode) => {
    if (React.isValidElement(child)) {
      // This accounts for non-flat structures containing fragments
      if (child.type === React.Fragment && child.props.children) {
        return recursiveMap(child.props.children, props);
      }
      return React.cloneElement(child as React.ReactElement, props);
    }
  });
};

export const DropdownMenu: React.FC<DropdownProps> = (props) => {
  const { menuItems, mobileTitle = "Action Menu", closeOnSelect, children, DropdownTriggerElement, desktopAlignment = "left" } = props;

  const { isMenuOpen, menuRef, onKeyUp, onKeyDown, onItemKeyUp, onClick, close, rover } = useKeyboardNav({
    isMenuOpen: false,
  });
  const [isManagingFocus, setManagingFocus] = React.useState(false);
  const dropdownMenuRef = React.useRef<HTMLDivElement>(null);
  const getBoundingClientRect = useGetBoundingClientRectRef();
  const [offsetY, maxHeight] = React.useMemo(() => {
    const innerHeight = window.innerHeight;
    const buffer = parseInt(style.vars.whitespace.large) + parseInt(style.vars.whitespace.xsmall);
    const { y, height, bottom } = getBoundingClientRect(dropdownMenuRef);
    const bottomWithBuffer = bottom + buffer;

    // If the dropdown is too big for the screen, reduce the size and let it overflow
    // If the screen is bigger, but the dropdown is beyond the scroll point, convert it to dropup
    // Else render unchanged
    if (height >= innerHeight) {
      return [0, y + buffer];
    } else {
      return [Math.max(bottomWithBuffer - innerHeight, 0), 0];
    }
  }, [isMenuOpen, dropdownMenuRef.current]);

  React.useEffect(() => {
    setManagingFocus(isMenuOpen);
  }, [isMenuOpen]);

  React.useEffect(() => {
    if (!isManagingFocus && !isMenuOpen) {
      close();
    }
  }, [isManagingFocus, isMenuOpen]);

  if (!menuItems && !children) {
    return null;
  }

  return (
    <DropdownWrapper>
      {DropdownTriggerElement ? (
        <DropdownTriggerElement
          isMenuOpen={isMenuOpen}
          onClick={onClick}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          ref={menuRef}
          tabIndex={0}
          data-testid={props["data-testid"]}
        />
      ) : (
        <DropdownIconWrapper
          role="button"
          aria-label="dropdown-trigger"
          onClick={onClick}
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          ref={menuRef}
          showHoverState={isMenuOpen}
          tabIndex={0}
          data-testid={props["data-testid"]}
        >
          <DropdownIcon icon="ellipsis" />
        </DropdownIconWrapper>
      )}
      <DropdownHandler
        desktopAlignment={desktopAlignment}
        mobileAlignment="fixed-bottom"
        inProp={isMenuOpen}
        mobileTitle={mobileTitle}
        clickAway={(e) => {
          close();
          e.stopPropagation();
        }}
      >
        <DropdownMenuWrapper
          ref={dropdownMenuRef}
          desktopAlignment={desktopAlignment}
          offsetY={offsetY}
          maxHeight={maxHeight}
          onClick={() => {
            if (closeOnSelect) {
              close();
            }
          }}
        >
          <DropdownMenuItems className="dropdown-wrap">
            {menuItems
              ? menuItems.map(({ title, action, disabled, danger, hasTopBorder, tooltip }) => (
                  <TooltipMenuItemWrapper key={title} tooltip={tooltip}>
                    <DropdownMenuItem role="menuitem">
                      <ListItemRoverItem
                        onKeyDown={onItemKeyUp}
                        onKeyUp={onItemKeyUp}
                        onClick={() => action(close)}
                        onFocus={() => setManagingFocus(true)}
                        onBlur={() => {
                          if (isManagingFocus) {
                            setManagingFocus(false);
                          }
                        }}
                        hasTopBorder={hasTopBorder}
                        disabled={disabled}
                        danger={danger}
                        {...rover}
                      >
                        <ListItem label={title} disabled={disabled} danger={danger}></ListItem>
                      </ListItemRoverItem>
                    </DropdownMenuItem>
                  </TooltipMenuItemWrapper>
                ))
              : recursiveMap(children, {
                  rover,
                  isManagingFocus,
                  setManagingFocus,
                })}
          </DropdownMenuItems>
        </DropdownMenuWrapper>
      </DropdownHandler>
    </DropdownWrapper>
  );
};

const StyledLink = styled(ReactRouterLink)<ActionItemProps & LinkStyleProps>`
  color: inherit;
  &:hover,
  &:active {
    color: ${({ $danger }) => ($danger ? style.colors.red.primary : style.colors.blue.primary)};
  }
`;

export const DropdownLink = styled(BaseLink)<ActionItemProps>`
  color: inherit;
  text-decoration: none;
  &:hover,
  &:active {
    color: ${({ danger }) => (danger ? style.colors.red.primary : style.colors.blue.primary)};
  }
  /* Overrides the styling occurring in LinkElem &&:active */
  &&:active:after {
    border-width: 0;
  }
`;

export const ActionItem: React.FC<CustomMenuItemProps> = (props) => (
  <TooltipMenuItemWrapper tooltip={props.tooltip}>
    <DropdownMenuItem role="menuitem">
      <GeneratedActionItem
        onClick={props.onClick}
        disabled={props.disabled}
        danger={props.danger}
        hasTopBorder={props.hasTopBorder}
        onFocus={() => props.setManagingFocus?.(true)}
        onBlur={() => {
          if (props.isManagingFocus) {
            props.setManagingFocus?.(false);
          }
        }}
        {...props.rover}
      >
        <ActionItemText>{props.children}</ActionItemText>
      </GeneratedActionItem>
    </DropdownMenuItem>
  </TooltipMenuItemWrapper>
);

export const InAppLinkItem: React.FC<ReactRouterLinkProps & CustomMenuItemProps> = ({
  rover,
  isManagingFocus,
  setManagingFocus,
  danger,
  hasTopBorder,
  tooltip,
  ...rest
}) => (
  <TooltipMenuItemWrapper tooltip={tooltip}>
    <DropdownMenuItem role="menuitem">
      <StyledLink tabIndex={-1} $danger={danger} $hasTopBorder={hasTopBorder} {...rest}>
        <GeneratedActionItem
          disabled={rest.disabled}
          danger={danger}
          hasTopBorder={hasTopBorder}
          onFocus={() => setManagingFocus?.(true)}
          onBlur={() => {
            if (isManagingFocus) {
              setManagingFocus?.(false);
            }
          }}
          {...rover}
        >
          <ActionItemText>{rest.children}</ActionItemText>
        </GeneratedActionItem>
      </StyledLink>
    </DropdownMenuItem>
  </TooltipMenuItemWrapper>
);

export const AuroraLinkItem: React.FC<LinkProps & CustomMenuItemProps> = (props) => (
  <TooltipMenuItemWrapper tooltip={props.tooltip}>
    <DropdownMenuItem role="menuitem">
      <DropdownLink tabIndex={-1} to={props.to} disabled={props.disabled} forceExternal target="_self">
        <GeneratedActionItem
          disabled={props.disabled}
          danger={props.danger}
          hasBorderTop={props.hasBorderTop}
          onFocus={() => props.setManagingFocus?.(true)}
          onBlur={() => {
            if (props.isManagingFocus) {
              props.setManagingFocus?.(false);
            }
          }}
          {...props.rover}
        >
          <ActionItemText>{props.children}</ActionItemText>
        </GeneratedActionItem>
      </DropdownLink>
    </DropdownMenuItem>
  </TooltipMenuItemWrapper>
);

export const Divider = styled.hr`
  border-top: ${style.vars.borderWidth.thin} solid ${style.colors.shadesOfGrey["4"]};
  margin-top: 0;
  margin-bottom: 0;
`;

const TooltipMenuItemWrapper: React.FC<ActionItemProps> = ({ tooltip, children }) => {
  let TooltipWrapper = React.Fragment as React.FC<{ content?: string }>;
  let tooltipProps = {};

  if (tooltip) {
    TooltipWrapper = Tooltip as React.FC<{ content?: string }>;
    tooltipProps = tooltip;
  }

  return <TooltipWrapper {...tooltipProps}>{children}</TooltipWrapper>;
};
